<template>
  <div class="project-select" :class="[`ps-size-${size}`, `ps-variant-${variant}`, { 'ps-block': block }]" ref="wrapperRef">
    <!-- Déclencheur -->
    <div
      class="ps-trigger"
      :class="{
        'is-open': isOpen,
        'has-value': !!selectedProject,
        'is-disabled': disabled,
        'is-invalid': invalid
      }"
      role="combobox"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      :aria-disabled="disabled"
      :tabindex="disabled ? -1 : 0"
      :title="selectedProject ? projectTitle(selectedProject) : ''"
      @click="toggleDropdown"
      @keydown.down.prevent="openDropdown"
      @keydown.enter.prevent="toggleDropdown"
      @keydown.space.prevent="toggleDropdown"
      @keydown.esc="closeDropdown"
    >
      <span class="ps-lead-icon" :class="{ 'is-active': !!selectedProject || !!selectedExtra }">
        <i class="bi" :class="selectedExtra ? (selectedExtra.icon || 'bi-asterisk') : (selectedProject ? 'bi-building-fill-check' : 'bi-building')"></i>
      </span>

      <div v-if="selectedProject" class="ps-value">
        <span class="ps-code">{{ selectedProject.name }}</span>
        <span v-if="projectSubtitle(selectedProject)" class="ps-desc">{{ projectSubtitle(selectedProject) }}</span>
      </div>
      <div v-else-if="selectedExtra" class="ps-value">
        <span class="ps-code">{{ selectedExtra.label }}</span>
        <span v-if="selectedExtra.description" class="ps-desc">{{ selectedExtra.description }}</span>
      </div>
      <div v-else class="ps-placeholder" :class="{ 'is-empty-choice': emptyLabel && isEmptyValue }">
        <template v-if="loading">
          <span class="spinner-border spinner-border-sm me-2"></span>Chargement des projets...
        </template>
        <template v-else>{{ emptyLabel && isEmptyValue ? emptyLabel : placeholder }}</template>
      </div>

      <div class="ps-actions">
        <button
          v-if="clearable && selectedProject && !disabled"
          type="button"
          class="ps-clear"
          title="Effacer la sélection"
          @click.stop="selectEmpty"
        >
          <i class="bi bi-x-lg"></i>
        </button>
        <i class="bi bi-chevron-down ps-arrow" :class="{ 'is-rotated': isOpen }"></i>
      </div>
    </div>

    <!-- Panneau déroulant (téléporté pour éviter tout rognage) -->
    <Teleport :to="teleportTarget">
      <Transition name="ps-pop">
        <div
          v-if="isOpen"
          ref="panelRef"
          class="ps-panel"
          :style="panelStyle"
          role="listbox"
          @click.stop
          @mousedown.stop
        >
          <div class="ps-search">
            <i class="bi bi-search ps-search-icon"></i>
            <input
              ref="searchRef"
              v-model="search"
              type="text"
              class="ps-search-input"
              :placeholder="searchPlaceholder"
              autocomplete="off"
              @keydown.down.prevent="moveFocus(1)"
              @keydown.up.prevent="moveFocus(-1)"
              @keydown.enter.prevent="selectFocused"
              @keydown.esc.prevent="closeDropdown(true)"
              @keydown.tab="closeDropdown()"
            />
            <button v-if="search" type="button" class="ps-search-clear" title="Effacer la recherche" @click="search = ''; searchRef?.focus()">
              <i class="bi bi-x"></i>
            </button>
          </div>

          <div class="ps-meta">
            <span>
              <strong>{{ filteredProjects.length }}</strong>
              {{ search ? `résultat${filteredProjects.length > 1 ? 's' : ''}` : `projet${filteredProjects.length > 1 ? 's' : ''}` }}
            </span>
            <span v-if="statusFilterActive" class="ps-filter-chip" :title="hiddenCount > 0 ? `${hiddenCount} projet(s) non en cours masqué(s)` : ''">
              <i class="bi bi-funnel-fill me-1"></i>En cours uniquement
            </span>
          </div>

          <div ref="listRef" class="ps-list">
            <!-- Option vide (Tous les projets / Aucun projet) -->
            <div
              v-if="emptyLabel && !search"
              class="ps-item ps-item-empty"
              :class="{ 'is-selected': isEmptyValue, 'is-focused': focusedIndex === -1 && hasEmptyFocus }"
              @click="selectEmpty"
              @mouseenter="focusedIndex = -1; hasEmptyFocus = true"
            >
              <span class="ps-item-icon"><i class="bi" :class="emptyIcon"></i></span>
              <span class="ps-item-body">
                <span class="ps-item-title">{{ emptyLabel }}</span>
              </span>
              <i v-if="isEmptyValue" class="bi bi-check-circle-fill ps-check"></i>
            </div>

            <!-- Options spéciales (ex: « Non affecté ») -->
            <div
              v-for="extra in (search ? [] : extraOptions)"
              :key="'extra-' + extra.value"
              class="ps-item ps-item-empty"
              :class="{ 'is-selected': isExtraSelected(extra) }"
              @click="selectExtra(extra)"
              @mouseenter="focusedIndex = -1; hasEmptyFocus = false"
            >
              <span class="ps-item-icon"><i class="bi" :class="extra.icon || 'bi-asterisk'"></i></span>
              <span class="ps-item-body">
                <span class="ps-item-title">{{ extra.label }}</span>
                <span v-if="extra.description" class="ps-item-sub">{{ extra.description }}</span>
              </span>
              <i v-if="isExtraSelected(extra)" class="bi bi-check-circle-fill ps-check"></i>
            </div>

            <div v-if="!search && (emptyLabel || extraOptions.length) && filteredProjects.length" class="ps-divider"></div>

            <div
              v-for="(project, index) in filteredProjects"
              :key="project.id"
              :data-index="index"
              class="ps-item"
              :class="{ 'is-selected': isSelected(project), 'is-focused': focusedIndex === index }"
              role="option"
              :aria-selected="isSelected(project)"
              @click="selectProject(project)"
              @mouseenter="focusedIndex = index; hasEmptyFocus = false"
            >
              <span class="ps-item-code" :class="{ 'is-solo': !projectSubtitle(project) }" v-html="highlight(project.name)"></span>
              <span class="ps-item-body">
                <span v-if="projectSubtitle(project)" class="ps-item-title" v-html="highlight(projectSubtitle(project))"></span>
                <span v-if="project.client_name && project.description" class="ps-item-sub">
                  <i class="bi bi-person-badge me-1"></i><span v-html="highlight(project.client_name)"></span>
                </span>
              </span>
              <span
                v-if="showStatus && statusMeta(project)"
                class="ps-status"
                :style="{ color: statusMeta(project).color, background: statusMeta(project).bg, borderColor: statusMeta(project).border }"
              >
                <i class="bi" :class="statusMeta(project).icon"></i>
                <span class="ps-status-label">{{ statusMeta(project).label }}</span>
              </span>
              <i v-if="isSelected(project)" class="bi bi-check-circle-fill ps-check"></i>
            </div>

            <div v-if="filteredProjects.length === 0" class="ps-empty-state">
              <div class="ps-empty-icon"><i class="bi" :class="search ? 'bi-search' : 'bi-folder-x'"></i></div>
              <div class="fw-semibold text-dark">Aucun projet trouvé</div>
              <div class="small text-muted" v-if="search">Aucun résultat pour « {{ search }} »</div>
              <div class="small text-muted" v-else-if="statusFilterActive && hiddenCount > 0">
                Seuls les projets en cours sont affichés.
              </div>
            </div>
          </div>

          <div class="ps-footer">
            <span><kbd>↑</kbd><kbd>↓</kbd> naviguer</span>
            <span><kbd>↵</kbd> choisir</span>
            <span><kbd>Échap</kbd> fermer</span>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useProjectSelectorSettings, PROJECT_STATUS_META } from '../../composables/useProjectSelectorSettings'

const props = defineProps({
  modelValue: { type: [Number, String, null], default: null },
  projects: { type: Array, default: () => [] },
  placeholder: { type: String, default: 'Sélectionner un projet...' },
  // Libellé de l'option « vide » (ex: « Tous les projets »). Absent = pas d'option vide.
  emptyLabel: { type: String, default: '' },
  // Valeur émise pour l'option vide
  emptyValue: { type: [Number, String, null], default: '' },
  emptyIcon: { type: String, default: 'bi-grid-3x3-gap' },
  // Options supplémentaires hors projets : [{ value, label, description?, icon? }]
  extraOptions: { type: Array, default: () => [] },
  searchPlaceholder: { type: String, default: 'Rechercher par nom, description, client...' },
  // Projets à exclure (ex: projet source dans une migration)
  excludeIds: { type: Array, default: () => [] },
  // Ignore le paramètre global « en cours uniquement »
  ignoreStatusFilter: { type: Boolean, default: false },
  showStatus: { type: Boolean, default: true },
  clearable: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  invalid: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  block: { type: Boolean, default: true },
  size: { type: String, default: 'md', validator: v => ['sm', 'md', 'lg'].includes(v) },
  // 'dark' : déclencheur translucide pour les en-têtes sombres
  variant: { type: String, default: 'default', validator: v => ['default', 'dark'].includes(v) }
})

const emit = defineEmits(['update:modelValue', 'change'])

const { inProgressOnly, loadProjectSelectorSettings, filterSelectableProjects } = useProjectSelectorSettings()

const isOpen = ref(false)
const search = ref('')
const focusedIndex = ref(-1)
const hasEmptyFocus = ref(false)
const wrapperRef = ref(null)
const panelRef = ref(null)
const searchRef = ref(null)
const listRef = ref(null)
const teleportTarget = ref('body')
const panelStyle = ref({})

// Place le panneau au-dessus du conteneur le plus haut (modales « maison » à z-index élevé)
function computePanelZIndex(el) {
  let max = 0
  for (let node = el; node && node !== document.body; node = node.parentElement) {
    const z = parseInt(window.getComputedStyle(node).zIndex, 10)
    if (!Number.isNaN(z)) max = Math.max(max, z)
  }
  return Math.max(2100, max + 10)
}

let panelZIndex = 2100

const sameId = (a, b) => a !== null && a !== undefined && a !== '' && String(a) === String(b)

const isEmptyValue = computed(() =>
  props.modelValue === null || props.modelValue === undefined || props.modelValue === '' ||
  String(props.modelValue) === String(props.emptyValue)
)

const selectedExtra = computed(() =>
  props.extraOptions.find(opt => String(opt.value) === String(props.modelValue)) || null
)

const selectedProject = computed(() => {
  if (isEmptyValue.value || selectedExtra.value) return null
  return props.projects.find(p => p && sameId(p.id, props.modelValue)) || null
})

const statusFilterActive = computed(() => inProgressOnly.value && !props.ignoreStatusFilter)

const selectableProjects = computed(() => {
  const excluded = new Set(props.excludeIds.filter(id => id !== null && id !== undefined && id !== '').map(String))
  const base = props.projects.filter(p => p && p.id !== undefined && !excluded.has(String(p.id)))
  return statusFilterActive.value ? filterSelectableProjects(base, [props.modelValue]) : base
})

const hiddenCount = computed(() => {
  if (!statusFilterActive.value) return 0
  const excluded = new Set(props.excludeIds.map(String))
  return props.projects.filter(p => p && !excluded.has(String(p.id))).length - selectableProjects.value.length
})

const normalize = (value) => String(value ?? '')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')

const filteredProjects = computed(() => {
  const q = normalize(search.value.trim())
  if (!q) return selectableProjects.value
  return selectableProjects.value.filter(p =>
    [p.name, p.description, p.client_name, p.code, p.location]
      .some(field => normalize(field).includes(q))
  )
})

function projectSubtitle(project) {
  if (project.description || project.client_name) return project.description || project.client_name
  // Les listes de simples noms (sans statut) n'ont pas de description à signaler
  return project.status ? 'Sans description' : ''
}

function projectTitle(project) {
  return [project.name, project.description].filter(Boolean).join(' — ')
}

function statusMeta(project) {
  return PROJECT_STATUS_META[project?.status] || null
}

function isSelected(project) {
  return !selectedExtra.value && sameId(project.id, props.modelValue)
}

function isExtraSelected(extra) {
  return !!selectedExtra.value && String(extra.value) === String(selectedExtra.value.value)
}

function selectExtra(extra) {
  if (!isExtraSelected(extra)) emitValue(extra.value, null)
  closeDropdown(true)
}

function escapeHtml(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

// Surligne la recherche en ignorant accents et casse
function highlight(text) {
  const raw = String(text ?? '')
  const q = normalize(search.value.trim())
  if (!q) return escapeHtml(raw)
  const start = normalize(raw).indexOf(q)
  if (start === -1) return escapeHtml(raw)
  const end = start + q.length
  return `${escapeHtml(raw.slice(0, start))}<mark>${escapeHtml(raw.slice(start, end))}</mark>${escapeHtml(raw.slice(end))}`
}

function updatePosition() {
  if (!wrapperRef.value) return
  const rect = wrapperRef.value.getBoundingClientRect()
  const vh = window.innerHeight
  const vw = window.innerWidth
  const gutter = 12
  const width = Math.min(Math.max(rect.width, 380), vw - gutter * 2)
  const left = Math.min(Math.max(gutter, rect.left), vw - width - gutter)
  const spaceBelow = vh - rect.bottom
  const openUp = spaceBelow < 340 && rect.top > spaceBelow
  const maxHeight = Math.max(220, Math.min(460, (openUp ? rect.top : spaceBelow) - gutter * 2))

  panelStyle.value = {
    position: 'fixed',
    left: `${left}px`,
    width: `${width}px`,
    maxHeight: `${maxHeight}px`,
    zIndex: panelZIndex,
    ...(openUp
      ? { bottom: `${vh - rect.top + 6}px`, top: 'auto', transformOrigin: 'bottom center' }
      : { top: `${rect.bottom + 6}px`, bottom: 'auto', transformOrigin: 'top center' })
  }
}

function toggleDropdown() {
  if (isOpen.value) closeDropdown()
  else openDropdown()
}

function openDropdown() {
  if (props.disabled || isOpen.value) return
  // Dans une modale Bootstrap, on reste dans la modale pour ne pas casser le piège de focus
  const fullscreenEl = document.fullscreenElement
  teleportTarget.value = wrapperRef.value?.closest('.modal') ||
    (fullscreenEl && fullscreenEl.contains(wrapperRef.value) ? fullscreenEl : 'body')
  panelZIndex = computePanelZIndex(wrapperRef.value)
  search.value = ''
  hasEmptyFocus.value = false
  focusedIndex.value = filteredProjects.value.findIndex(p => isSelected(p))
  updatePosition()
  isOpen.value = true
  window.addEventListener('resize', updatePosition, { passive: true })
  window.addEventListener('scroll', onScroll, { passive: true, capture: true })
  nextTick(() => {
    updatePosition()
    searchRef.value?.focus({ preventScroll: true })
    scrollFocusedIntoView()
  })
}

function closeDropdown(refocus = false) {
  if (!isOpen.value) return
  isOpen.value = false
  search.value = ''
  window.removeEventListener('resize', updatePosition)
  window.removeEventListener('scroll', onScroll, true)
  if (refocus === true) {
    wrapperRef.value?.querySelector('.ps-trigger')?.focus({ preventScroll: true })
  }
}

function onScroll(event) {
  // Le défilement de la liste elle-même ne doit pas repositionner le panneau
  if (panelRef.value && panelRef.value.contains(event.target)) return
  updatePosition()
}

function emitValue(value, project) {
  emit('update:modelValue', value)
  emit('change', value, project)
}

function selectProject(project) {
  if (!isSelected(project)) emitValue(project.id, project)
  closeDropdown(true)
}

function selectEmpty() {
  if (!isEmptyValue.value) emitValue(props.emptyValue, null)
  closeDropdown(true)
}

function moveFocus(step) {
  const count = filteredProjects.value.length
  if (!count) return
  hasEmptyFocus.value = false
  let next = focusedIndex.value + step
  if (next < 0) next = count - 1
  if (next >= count) next = 0
  focusedIndex.value = next
  scrollFocusedIntoView()
}

function selectFocused() {
  if (hasEmptyFocus.value) return selectEmpty()
  const list = filteredProjects.value
  const target = list[focusedIndex.value] || (list.length === 1 || search.value ? list[0] : null)
  if (target) selectProject(target)
}

function scrollFocusedIntoView() {
  nextTick(() => {
    const el = listRef.value?.querySelector(`[data-index="${focusedIndex.value}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  })
}

function onDocumentPointerDown(event) {
  if (!isOpen.value) return
  const inTrigger = wrapperRef.value?.contains(event.target)
  const inPanel = panelRef.value?.contains(event.target)
  if (!inTrigger && !inPanel) closeDropdown()
}

onMounted(() => {
  loadProjectSelectorSettings()
  document.addEventListener('mousedown', onDocumentPointerDown)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocumentPointerDown)
  window.removeEventListener('resize', updatePosition)
  window.removeEventListener('scroll', onScroll, true)
})
</script>

<style scoped>
.project-select {
  position: relative;
  display: inline-block;
  min-width: 220px;
}

.project-select.ps-block {
  display: block;
  width: 100%;
  min-width: 0;
}

/* ---------- Déclencheur ---------- */
.ps-trigger {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  width: 100%;
  min-height: 42px;
  padding: 0.35rem 0.75rem 0.35rem 0.4rem;
  background: #fff;
  border: 1px solid #d0d7e2;
  border-radius: 0.65rem;
  cursor: pointer;
  user-select: none;
  outline: none;
  text-align: left;
  color: #0f172a;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
}

.ps-trigger:hover:not(.is-disabled) {
  border-color: #818cf8;
}

.ps-trigger:focus-visible,
.ps-trigger.is-open {
  border-color: #6366f1;
  box-shadow: 0 0 0 0.2rem rgba(99, 102, 241, 0.18);
}

.ps-trigger.is-invalid {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.15);
}

.ps-trigger.is-disabled {
  background: #f1f5f9;
  cursor: not-allowed;
  opacity: 0.7;
}

.ps-lead-icon {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 0.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  color: #64748b;
  font-size: 0.95rem;
  transition: all 0.15s ease;
}

.ps-lead-icon.is-active {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: #fff;
  box-shadow: 0 2px 6px rgba(99, 102, 241, 0.3);
}

.ps-value {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.ps-code {
  font-weight: 700;
  font-size: 0.85rem;
  color: #1e1b4b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ps-desc {
  font-size: 0.74rem;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ps-placeholder {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.875rem;
  color: #94a3b8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ps-placeholder.is-empty-choice {
  color: #334155;
  font-weight: 600;
}

.ps-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
}

.ps-clear {
  border: none;
  background: transparent;
  color: #94a3b8;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  padding: 0;
}

.ps-clear:hover {
  background: #fee2e2;
  color: #dc2626;
}

.ps-arrow {
  color: #64748b;
  font-size: 0.8rem;
  transition: transform 0.2s ease;
}

.ps-arrow.is-rotated {
  transform: rotate(180deg);
}

/* Tailles */
.ps-size-sm .ps-trigger {
  min-height: 31px;
  padding: 0.15rem 0.6rem 0.15rem 0.25rem;
  gap: 0.45rem;
  border-radius: 0.5rem;
}
.ps-size-sm .ps-lead-icon {
  width: 24px;
  height: 24px;
  font-size: 0.8rem;
  border-radius: 0.4rem;
}
.ps-size-sm .ps-value {
  flex-direction: row;
  align-items: baseline;
  gap: 0.4rem;
}
.ps-size-sm .ps-code { font-size: 0.8rem; flex-shrink: 0; max-width: 45%; }
.ps-size-sm .ps-desc { font-size: 0.75rem; }
.ps-size-sm .ps-placeholder { font-size: 0.8rem; }

.ps-size-lg .ps-trigger {
  min-height: 50px;
  padding: 0.45rem 0.9rem 0.45rem 0.5rem;
  border-radius: 0.75rem;
}
.ps-size-lg .ps-lead-icon { width: 36px; height: 36px; font-size: 1.1rem; }
.ps-size-lg .ps-code { font-size: 0.95rem; }
.ps-size-lg .ps-desc { font-size: 0.8rem; }
.ps-size-lg .ps-placeholder { font-size: 0.95rem; }

/* Variante sombre (en-têtes colorés) */
.ps-variant-dark .ps-trigger {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
  color: #fff;
}
.ps-variant-dark .ps-trigger:hover:not(.is-disabled) { border-color: rgba(255, 255, 255, 0.45); }
.ps-variant-dark .ps-trigger.is-open { border-color: #fff; box-shadow: 0 0 0 0.2rem rgba(255, 255, 255, 0.15); }
.ps-variant-dark .ps-lead-icon { background: rgba(255, 255, 255, 0.12); color: #fff; }
.ps-variant-dark .ps-code,
.ps-variant-dark .ps-placeholder.is-empty-choice { color: #fff; }
.ps-variant-dark .ps-desc,
.ps-variant-dark .ps-placeholder,
.ps-variant-dark .ps-arrow { color: rgba(255, 255, 255, 0.7); }

/* ---------- Panneau ---------- */
.ps-panel {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.9rem;
  box-shadow: 0 20px 45px -12px rgba(15, 23, 42, 0.28), 0 4px 12px rgba(15, 23, 42, 0.06);
  overflow: hidden;
  color: #0f172a;
  text-align: left;
  font-size: 0.875rem;
}

.ps-search {
  position: relative;
  padding: 0.65rem 0.65rem 0.4rem;
}

.ps-search-icon {
  position: absolute;
  left: 1.3rem;
  top: 50%;
  transform: translateY(-30%);
  color: #6366f1;
  font-size: 0.85rem;
  pointer-events: none;
}

.ps-search-input {
  width: 100%;
  height: 38px;
  padding: 0 2.2rem 0 2.2rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.6rem;
  background: #f8fafc;
  font-size: 0.85rem;
  color: #0f172a;
  outline: none;
  transition: all 0.15s ease;
}

.ps-search-input:focus {
  background: #fff;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.ps-search-clear {
  position: absolute;
  right: 1.1rem;
  top: 50%;
  transform: translateY(-30%);
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 50%;
  background: #e2e8f0;
  color: #475569;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.ps-search-clear:hover {
  background: #cbd5e1;
}

.ps-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0 0.95rem 0.45rem;
  font-size: 0.72rem;
  color: #64748b;
}

.ps-filter-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.12rem 0.5rem;
  border-radius: 999px;
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
  font-weight: 600;
  white-space: nowrap;
}

.ps-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0.25rem 0.45rem 0.45rem;
  border-top: 1px solid #f1f5f9;
  overscroll-behavior: contain;
}

.ps-list::-webkit-scrollbar { width: 6px; }
.ps-list::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }

.ps-item {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.5rem 0.6rem;
  margin-top: 0.2rem;
  border-radius: 0.6rem;
  border: 1px solid transparent;
  cursor: pointer;
  transition: background-color 0.12s ease, border-color 0.12s ease;
}

.ps-item.is-focused {
  background: #f5f3ff;
  border-color: #e0e7ff;
}

.ps-item.is-selected {
  background: #eef2ff;
  border-color: #c7d2fe;
}

.ps-item-code {
  flex-shrink: 0;
  max-width: 40%;
  margin-right: auto;
  padding: 0.22rem 0.55rem;
  border-radius: 0.45rem;
  background: #eef2ff;
  color: #4338ca;
  border: 1px solid #e0e7ff;
  font-weight: 700;
  font-size: 0.76rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ps-item-code.is-solo {
  max-width: none;
  flex-shrink: 1;
}

.ps-item.is-selected .ps-item-code {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border-color: transparent;
  color: #fff;
}

.ps-item-body {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}

.ps-item-title {
  font-weight: 500;
  font-size: 0.83rem;
  color: #1e293b;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.ps-item.is-selected .ps-item-title {
  font-weight: 600;
  color: #312e81;
}

.ps-item-sub {
  margin-top: 0.1rem;
  font-size: 0.7rem;
  color: #94a3b8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ps-item-empty .ps-item-title {
  font-weight: 600;
  color: #334155;
}

.ps-divider {
  height: 1px;
  margin: 0.35rem 0.3rem 0.1rem;
  background: #eef2f7;
}

.ps-item-icon {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 0.45rem;
  background: #f1f5f9;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.ps-status {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.12rem 0.45rem;
  border-radius: 999px;
  border: 1px solid;
  font-size: 0.66rem;
  font-weight: 600;
  white-space: nowrap;
}

.ps-check {
  flex-shrink: 0;
  color: #6366f1;
  font-size: 1rem;
}

.ps-empty-state {
  text-align: center;
  padding: 1.5rem 1rem;
}

.ps-empty-icon {
  width: 44px;
  height: 44px;
  margin: 0 auto 0.5rem;
  border-radius: 50%;
  background: #f1f5f9;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
}

.ps-footer {
  display: flex;
  gap: 0.9rem;
  padding: 0.4rem 0.95rem;
  border-top: 1px solid #f1f5f9;
  background: #f8fafc;
  font-size: 0.68rem;
  color: #94a3b8;
}

.ps-footer kbd {
  display: inline-block;
  min-width: 18px;
  margin-right: 0.15rem;
  padding: 0.05rem 0.3rem;
  border: 1px solid #e2e8f0;
  border-bottom-width: 2px;
  border-radius: 0.3rem;
  background: #fff;
  color: #475569;
  font-family: inherit;
  font-size: 0.66rem;
  font-weight: 600;
  text-align: center;
  box-shadow: none;
}

.ps-panel :deep(mark) {
  padding: 0 0.1rem;
  border-radius: 0.2rem;
  background: #fef08a;
  color: inherit;
}

@media (max-width: 480px) {
  .ps-status-label { display: none; }
  .ps-footer { display: none; }
}

/* Animation */
.ps-pop-enter-active,
.ps-pop-leave-active {
  transition: opacity 0.15s ease, transform 0.15s cubic-bezier(0.16, 1, 0.3, 1);
}

.ps-pop-enter-from,
.ps-pop-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}
</style>
